import { useIntl } from 'react-intl';

import { mapApiErrorCode } from '@/errorCodes/helpers';

import { Complimentary } from '@/components/Complimentary/Complimentary';
import { Flex } from '@/components/Flex';
import { useCompoundContext } from '@/components/Header/context';
import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import { LegacyButton } from '@/components/LegacyButton';
import { SalesTaxBadge } from '@/components/SalesTaxBadge';
import { getFormattedMessage } from '@/components/ServerErrors';
import { Typography } from '@/components/Typography';
import type { CheckoutLineFragment } from '@/graphql/saleor/api';
import { useCheckout } from '@/hooks/useCheckout';
import { useOnRouteChange } from '@/hooks/useOnRouteChange';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import {
  getLinesIdWithNotSelectedBraceletSize,
  getLineSubheader,
} from '@/lib/checkout';
import { getTranslatedName } from '@/lib/i18n';
import { sumPrices } from '@/lib/prices';
import type { ProductTypeOptions } from '@/lib/product';
import {
  excludeProductType,
  getImageScaleRatio,
  getProductType,
} from '@/lib/product';
import type { ApiErrorWithType } from '@/lib/types';
import { checkoutMessages } from '@/messages/checkout';
import { commonMessages } from '@/messages/common';
import type { CheckoutState } from '@/states/checkout';
import { checkoutActions } from '@/states/checkout';

import * as S from './AddToBagDialog.styles';
import * as M from './messages';

interface AddToBagDialogProps {
  className?: string;
}

export const isErrorNotification = (
  notification: CheckoutState['notification'],
): notification is ApiErrorWithType[] =>
  Array.isArray(notification) &&
  // @ts-ignore
  notification?.[0]?.__typename?.endsWith('Error');

const isCheckoutLineNotification = (
  notification: CheckoutState['notification'],
): notification is CheckoutLineFragment[] =>
  Array.isArray(notification) &&
  // @ts-ignore
  notification?.[0]?.__typename === 'CheckoutLine';

export const AddToBagDialog = ({ className }: AddToBagDialogProps) => {
  const { formatMessage } = useIntl();
  const {
    snapshot: { checkout, linesCount, notification },
  } = useCheckout();
  const {
    snapshot: { isAuthenticated },
  } = useUser();
  const { paths } = useRouter();
  useCompoundContext();

  const handleCloseDialog = () => checkoutActions.notify(null);

  useOnRouteChange(() => checkoutActions.notify(null));

  const showCheckoutButton = !getLinesIdWithNotSelectedBraceletSize(
    checkout?.lines,
  ).length;

  if (!notification) {
    return null;
  }

  return (
    <S.Wrapper className={className} data-test-id='AddToBagDialogWrapper'>
      <S.HeaderWrapper>
        <S.Header>
          <S.CheckIcon />
          <S.HeaderText>
            {formatMessage(checkoutMessages.checkoutAddPopinSuccess)}
          </S.HeaderText>
        </S.Header>
        <S.CloseButton
          data-test-id='AddToBagDialogCloseButton'
          onClick={handleCloseDialog}
        >
          <S.CloseIcon />
        </S.CloseButton>
      </S.HeaderWrapper>

      {notification && (
        <S.Body data-test-id='AddToBagDialogBody'>
          {typeof notification === 'string' && (
            <S.AlreadyInBagText data-test-id='AddToBagDialogAlreadyInBagText'>
              {notification}
            </S.AlreadyInBagText>
          )}
          {isErrorNotification(notification) &&
            notification.map((error, i) => {
              const message = getFormattedMessage(
                error,
                formatMessage,
                mapApiErrorCode,
              );
              if (!message) {
                return;
              }
              return (
                <Typography key={i} data-test-id={`AddToBagDialogError-${i}`}>
                  {message}
                </Typography>
              );
            })}
          {isCheckoutLineNotification(notification) &&
            (notification as CheckoutLineFragment[])
              .filter((item) =>
                excludeProductType(item.variant.product.productType, 'buckle'),
              )
              .map(({ id, relatedProductBuckleId, unitPrice, variant }, i) => {
                const buckle = checkout?.lines?.find(
                  (item) => item.id === relatedProductBuckleId,
                );
                const price =
                  sumPrices(unitPrice, buckle?.unitPrice) ?? unitPrice;

                return (
                  <Flex key={id} data-test-id={`AddToBagDialogLine-${i}`}>
                    <S.ImageWrapper
                      data-test-id={`AddToBagDialogImageWrapper-${i}`}
                    >
                      {variant.product?.thumbnail ? (
                        <S.Image
                          $scaleRatio={getImageScaleRatio(
                            variant.product?.productType,
                          )}
                          alt={variant.product?.thumbnail?.alt ?? ''}
                          fill
                          sizes='10vw'
                          src={variant.product?.thumbnail?.url ?? ''}
                        />
                      ) : (
                        <ImagePlaceholder
                          productType={
                            variant.product.productType.name.toLowerCase() as ProductTypeOptions
                          }
                        />
                      )}
                    </S.ImageWrapper>
                    <S.RowContent
                      data-test-id={`AddToBagDialogRowContent-${i}`}
                    >
                      <Flex
                        flexDirection='column'
                        justifyContent='space-between'
                      >
                        <S.Sku>{variant.sku}</S.Sku>
                        <S.Name>{variant.product.name}</S.Name>
                        <S.AdditionalName
                          data-test-id={`AddToBagDialogAdditionalName-${i}`}
                        >
                          {getLineSubheader(variant.product, variant.sku)}
                        </S.AdditionalName>
                        {buckle && (
                          <S.AdditionalName
                            data-test-id={`AddToBagDialogBuckleAdditionalName-${i}`}
                          >
                            {getTranslatedName('name', buckle.variant.product)}
                          </S.AdditionalName>
                        )}
                      </Flex>
                      <Flex flexDirection='column' justifyContent='end'>
                        {getProductType(variant.product.productType) ===
                        'gift' ? (
                          <Complimentary />
                        ) : (
                          <>
                            <S.Price
                              channel={checkout?.channel?.slug}
                              displayZero
                              price={price}
                            />
                            <SalesTaxBadge channel={checkout?.channel?.slug} />
                          </>
                        )}
                      </Flex>
                    </S.RowContent>
                  </Flex>
                );
              })}
        </S.Body>
      )}
      <S.Footer data-test-id='AddToBagDialogFooter'>
        <LegacyButton
          data-test-id='AddToBagDialogViewButton'
          isFluid
          label={formatMessage(M.messages.view, {
            itemCount: linesCount,
          })}
          linkProps={{ href: paths.store.shoppingBag.url() }}
          variant='outlined'
        />
        {showCheckoutButton && (
          <LegacyButton
            data-test-id='AddToBagDialogCheckoutButton'
            isFluid
            label={formatMessage(commonMessages.checkout)}
            linkProps={{
              href: isAuthenticated
                ? paths.store.checkout.delivery.asPath()
                : paths.myAccount.login.url({
                    query: {
                      next: paths.store.checkout.delivery.asPath(),
                      variant: 'checkout',
                    },
                  }),
            }}
          />
        )}
      </S.Footer>
    </S.Wrapper>
  );
};

AddToBagDialog.displayName = 'AddToBagDialog';
